import React, { useEffect, useState } from 'react';
import { focusFixField } from '../../utils/focusFixField';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Switch,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  VirtualizedCombobox,
} from '@roger/ui-components';
import { Loader2, RefreshCw, Edit, Plus, Unlink, Link2 } from 'lucide-react';
import {
  disconnectClinicFromKinnser,
  reconnectClinicToKinnser,
  syncClinicsFromClinicInfo,
  createAgencyClinic,
  getAgencyInsurances,
  type AgencyClinicResponse,
} from '../../services/clinicsApi';
import {
  checkClinicOasisExportErrorsAgencyClinicsOasisExportErrorsPost,
  updateAgencyClinicAgencyClinicsAgencyClinicIdPut,
} from '../../services/api/sdk.gen';
import { useCmsRecordCheck } from '../../hooks/useCmsRecordCheck';
import { CmsFieldError } from '../../components/oasis/CmsFieldError';
import {
  SHP_UNSENDABLE_REASONS,
  cmsErrorsByField,
  cmsFieldErrorId,
} from '../../components/oasis/cmsFieldErrors';
import { US_STATES } from '../../constants/options';
import { NpiInput } from '../../components/NpiInput';
import { sanitizeNpiInput, validateNpi } from '../../utils/npiValidation';
import { validateCcn } from '../../utils/ccnValidation';
import { formatPhone, formatStoredPhone } from '../../utils/intake';
import { runValidation } from '../../components/intake/validators';
import { useAdminSectionAccess } from '../../hooks/useAdminSectionAccess';
import { clinicParticipatesInKinnser } from '../../utils/clinicKeyUtils';
import { ClinicMappedToKinnser } from '../../components/admin/ClinicMappedToKinnser';
import { TIMEZONE_OPTIONS } from '../../utils/timezoneOptions';

type ClinicEditForm = {
  name?: string;
  npi?: string;
  ein?: string;
  address?: string;
  city?: string;
  state?: string | null;
  zip_code?: string;
  phone?: string;
  fax?: string;
  email?: string;
  hha_agency_id?: string;
  cms_qies_agency_id?: string;
  cms_branch_id?: string;
  shp_provider_no?: string;
  billing_contact_first_name?: string;
  billing_contact_last_name?: string;
  billing_contact_phone?: string;
  use_kinnser_wrm?: boolean;
  auto_mrn?: boolean;
  using_rcd?: boolean;
  default_intake_emr_is_roger?: boolean;
  timezone?: string | null;
};

interface ClinicInfoSectionProps {
  clinics: AgencyClinicResponse[];
  isLoadingClinics: boolean;
  isSuperUser: boolean;
  isRogerEmrEnabled: boolean;
  isKinnserEnabled: boolean;
  /** Reload clinics after mutations / sync; also syncs the shared store. */
  onReloadClinics: () => Promise<void>;
  /** After clinic-info sync succeeds — parent bumps sibling section refresh tokens. */
  onSyncComplete?: () => void;
  /** A clinic whose edit dialog should open on arrival (`?editClinic=<id>`). */
  requestedEditClinicId?: string | null;
  /** The field in that dialog a fix link names (`?fixField=<id>`), focused on open. */
  requestedFieldId?: string | null;
  /** Called once that request has been acted on, so the parent can drop it. */
  onEditClinicRequestHandled?: () => void;
}

const ClinicInfoSection: React.FC<ClinicInfoSectionProps> = ({
  clinics,
  isLoadingClinics,
  isSuperUser,
  isRogerEmrEnabled,
  isKinnserEnabled,
  onReloadClinics,
  onSyncComplete,
  requestedEditClinicId,
  requestedFieldId,
  onEditClinicRequestHandled,
}) => {
  const { canEdit } = useAdminSectionAccess('clinic-info');
  const { user, showSuccess, showError } = useStore(
    useShallow(state => ({
      user: state.user,
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [isLoadingClinicInfo, setIsLoadingClinicInfo] = useState(false);
  const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
  const [clinicModalMode, setClinicModalMode] = useState<'create' | 'edit'>(
    'edit'
  );
  const [editingClinicId, setEditingClinicId] = useState<string | null>(null);
  const [editingClinicHasKey, setEditingClinicHasKey] = useState(false);
  const [clinicEditForm, setClinicEditForm] = useState<ClinicEditForm>({});
  const [isSavingClinic, setIsSavingClinic] = useState(false);

  // What CMS and the nightly SHP batch would refuse in the open dialog's
  // values, judged as typed and shown under each field.
  const cmsCheck = useCmsRecordCheck(
    isClinicModalOpen && isRogerEmrEnabled && canEdit
      ? {
          npi: sanitizeNpiInput(clinicEditForm.npi || '') || null,
          cms_qies_agency_id: clinicEditForm.cms_qies_agency_id?.trim() || null,
          state: clinicEditForm.state || null,
          shp_provider_no: clinicEditForm.shp_provider_no?.trim() || null,
          agency_clinic_id: editingClinicId || null,
        }
      : null,
    async body =>
      (
        await checkClinicOasisExportErrorsAgencyClinicsOasisExportErrorsPost({
          body,
        })
      ).data
  );
  const cmsErrors = cmsErrorsByField(cmsCheck?.errors ?? []);
  const shpReason = cmsCheck?.shp_provider_number_reason;
  if (shpReason) cmsErrors.shp_provider_no = SHP_UNSENDABLE_REASONS[shpReason];
  // The API refuses a new non-digit number for every agency, so the check is
  // local. A stored one left as it is still saves: it only warns.
  const typedShpNumber = clinicEditForm.shp_provider_no?.trim() ?? '';
  const storedShpNumber =
    clinicModalMode === 'edit'
      ? (clinics.find(c => c.id === editingClinicId)?.shp_provider_no?.trim() ??
        '')
      : '';
  const shpNumberNotDigits =
    typedShpNumber !== '' && !/^[0-9]+$/.test(typedShpNumber);
  const shpNumberUnsaveable =
    shpNumberNotDigits && typedShpNumber !== storedShpNumber;
  if (shpNumberNotDigits && !cmsErrors.shp_provider_no)
    cmsErrors.shp_provider_no =
      SHP_UNSENDABLE_REASONS.provider_number_not_numeric;

  // Kinnser participation confirmation. Holds the clinic being changed and the
  // direction, so one dialog serves both actions.
  const [kinnserParticipationTarget, setKinnserParticipationTarget] = useState<{
    clinic: AgencyClinicResponse;
    action: 'disconnect' | 'reconnect';
  } | null>(null);
  const [isChangingKinnserParticipation, setIsChangingKinnserParticipation] =
    useState(false);

  // Insurance counts for the Mapped-to-Kinnser tooltip. Owned here so the
  // insurance tab can keep its own list state.
  const [insuranceCountByClinicId, setInsuranceCountByClinicId] = useState<
    Map<string, number>
  >(new Map());

  // The Kinnser column also shows the Disconnected state, so it stays visible
  // for an agency that has finished a cutover and turned the agency-level
  // Kinnser toggle off. Otherwise the record of what was disconnected would
  // disappear from the table.
  const showKinnserColumn =
    isKinnserEnabled ||
    clinics.some(clinic => !!clinic.kinnser_disconnected_at);

  // Colspan for loading/empty rows:
  //   Base: 6 (Name, Clinic Key, NPI, EIN, Address, Actions)
  //   Kinnser: +1 Mapped to Kinnser?
  const clinicTableColSpan = showKinnserColumn ? 7 : 6;

  const loadInsuranceCounts = async () => {
    if (!user?.agency?.id) return;
    try {
      const data = await getAgencyInsurances(user.agency.id);
      const counts = new Map<string, number>();
      for (const insurance of data) {
        const clinicId = insurance.agency_clinic_id;
        if (!clinicId) continue;
        counts.set(clinicId, (counts.get(clinicId) ?? 0) + 1);
      }
      setInsuranceCountByClinicId(counts);
    } catch (error) {
      console.error('Failed to load insurance counts for clinic map:', error);
    }
  };

  useEffect(() => {
    if (user?.agency?.id) {
      loadInsuranceCounts();
    }
  }, [user?.agency?.id]);

  // Open the requested clinic's edit dialog once the clinics have loaded. The
  // list starts empty before the first load, so an empty list is "not loaded
  // yet", not "no such clinic". A viewer who cannot edit, or an id that is not
  // one of this agency's clinics, gets the plain list.
  useEffect(() => {
    if (!requestedEditClinicId || isLoadingClinics || clinics.length === 0) {
      return;
    }
    const clinic = clinics.find(entry => entry.id === requestedEditClinicId);
    if (clinic && canEdit) {
      handleEditClinic(clinic);
      if (requestedFieldId) focusFixField(requestedFieldId);
    }
    onEditClinicRequestHandled?.();
  }, [requestedEditClinicId, isLoadingClinics, clinics, canEdit]);

  const handleConfirmKinnserParticipationChange = async () => {
    if (!kinnserParticipationTarget) return;
    const { clinic, action } = kinnserParticipationTarget;

    setIsChangingKinnserParticipation(true);
    try {
      if (action === 'disconnect') {
        await disconnectClinicFromKinnser(clinic.id);
        showSuccess(
          'Disconnected from WellSky sync',
          `Roger now manages ${clinic.name}. Its clinic key was kept, so it can be reconnected.`
        );
      } else {
        await reconnectClinicToKinnser(clinic.id);
        showSuccess(
          'Reconnected to WellSky sync',
          `WellSky manages ${clinic.name} again.`
        );
      }
      setKinnserParticipationTarget(null);
    } catch (error) {
      console.error('Failed to change Kinnser participation:', error);
      showError(
        action === 'disconnect'
          ? 'Unable to disconnect this clinic from WellSky sync. Please try again.'
          : 'Unable to reconnect this clinic to WellSky sync. Please try again.'
      );
      return;
    } finally {
      setIsChangingKinnserParticipation(false);
    }

    // Deliberately after the try, not inside it. Both halves of this refresh
    // swallow and report their own failures today, so nothing here can reject,
    // but keeping it outside means a refresh that starts propagating later
    // cannot be reported as a failed disconnect. Refreshes the shared store as
    // well as this table, because the Add Insurance clinic picker reads the
    // store.
    await onReloadClinics();
  };

  // Handle get clinic info
  const handleSyncClinicInfo = async () => {
    if (!user?.agency?.system_name) {
      showError('No agency system name found');
      return;
    }

    setIsLoadingClinicInfo(true);

    try {
      // Sync clinic info to database (this now fetches from RogerDoc API internally)
      const syncResult = await syncClinicsFromClinicInfo();
      showSuccess(
        `Clinic, insurance, physician, and referral source information synced successfully. ` +
          `Clinics: ${syncResult.clinics.created_count} created, ${syncResult.clinics.updated_count} updated. ` +
          `Insurances: ${syncResult.insurances.created_count} created, ${syncResult.insurances.updated_count} updated. ` +
          `Physicians: ${syncResult.physicians.created_count} created, ${syncResult.physicians.updated_count} updated. ` +
          `Referral sources: ${syncResult.internal_referral_sources.created_count} created, ${syncResult.internal_referral_sources.updated_count} updated.`
      );
      await onReloadClinics(); // Reload clinics after successful sync
      await loadInsuranceCounts(); // Reload Mapped-to-Kinnser insurance counts
      onSyncComplete?.(); // Reload physicians / referral sources / insurance tabs
    } catch (error) {
      console.error('Failed to get clinic info:', error);

      // Scraping disabled - show error instead
      showError(
        'Failed to retrieve clinic information. Scraping is currently disabled.'
      );
    } finally {
      setIsLoadingClinicInfo(false);
    }
  };

  // Handle opening create clinic dialog
  const handleCreateClinic = () => {
    setClinicModalMode('create');
    setEditingClinicId(null);
    setEditingClinicHasKey(false);
    setClinicEditForm({
      name: '',
      use_kinnser_wrm: false,
      auto_mrn: false,
      using_rcd: false,
      default_intake_emr_is_roger: false,
    });
    setIsClinicModalOpen(true);
  };

  // Normalize EIN to digits-only (Stedi rejects hyphens/other chars)
  const normalizeEin = (value: string) => value.replace(/\D/g, '');

  // Handle opening edit clinic dialog
  const handleEditClinic = (clinic: AgencyClinicResponse) => {
    setClinicModalMode('edit');
    setEditingClinicId(clinic.id);
    setEditingClinicHasKey(clinicParticipatesInKinnser(clinic));
    setClinicEditForm({
      name: clinic.name || '',
      npi: clinic.npi || '',
      ein: normalizeEin(clinic.ein || ''),
      address: clinic.address || '',
      city: clinic.city || '',
      state: clinic.state || '',
      zip_code: clinic.zip_code || '',
      phone: formatStoredPhone(clinic.phone),
      fax: formatStoredPhone(clinic.fax),
      email: clinic.email || '',
      hha_agency_id: clinic.hha_agency_id || '',
      cms_qies_agency_id: clinic.cms_qies_agency_id || '',
      cms_branch_id: clinic.cms_branch_id || '',
      shp_provider_no: clinic.shp_provider_no || '',
      billing_contact_first_name: clinic.billing_contact_first_name || '',
      billing_contact_last_name: clinic.billing_contact_last_name || '',
      billing_contact_phone: formatStoredPhone(clinic.billing_contact_phone),
      use_kinnser_wrm: clinic.use_kinnser_wrm ?? false,
      auto_mrn: clinic.auto_mrn ?? false,
      using_rcd: clinic.using_rcd ?? false,
      default_intake_emr_is_roger: clinic.default_intake_emr_is_roger ?? false,
      timezone: clinic.timezone ?? null,
    });
    setIsClinicModalOpen(true);
  };

  // Handle clinic save (create or update)
  const handleSaveClinic = async () => {
    const trimmedClinicName = clinicEditForm.name?.trim() ?? '';

    if (clinicModalMode === 'create') {
      if (!trimmedClinicName) {
        showError('Clinic name is required');
        return;
      }
    } else if (!editingClinicId) {
      return;
    }

    const npiSanitized = sanitizeNpiInput(clinicEditForm.npi || '');
    const npiValidationError = validateNpi(npiSanitized);
    if (npiValidationError) {
      showError(npiValidationError);
      return;
    }

    const ccnValidationError = validateCcn(clinicEditForm.hha_agency_id || '');
    if (ccnValidationError) {
      showError(ccnValidationError);
      return;
    }

    const phoneValidationError = runValidation(
      'validate_phone',
      clinicEditForm.phone
    );
    if (phoneValidationError) {
      showError(phoneValidationError);
      return;
    }

    // Reuses the phone validator for the shared aaa-bbb-cccc shape, but states
    // the field itself: forwarding that validator's own text would read "Phone
    // must be in format ..." under a Fax label.
    if (runValidation('validate_phone', clinicEditForm.fax)) {
      showError('Fax must be in format aaa-bbb-cccc');
      return;
    }

    const billingContactPhoneError = runValidation(
      'validate_phone',
      clinicEditForm.billing_contact_phone
    );
    if (billingContactPhoneError) {
      showError(`Billing contact: ${billingContactPhoneError}`);
      return;
    }

    if (clinicModalMode === 'create') {
      if (!user?.agency?.id) return;

      setIsSavingClinic(true);
      try {
        await createAgencyClinic({
          agency_id: user.agency.id,
          name: trimmedClinicName,
          npi: npiSanitized || undefined,
          ein: normalizeEin(clinicEditForm.ein || '') || undefined,
          address: clinicEditForm.address || undefined,
          city: clinicEditForm.city || undefined,
          state: clinicEditForm.state || undefined,
          zip_code: clinicEditForm.zip_code || undefined,
          phone: clinicEditForm.phone || undefined,
          fax: clinicEditForm.fax || undefined,
          email: clinicEditForm.email?.trim() || undefined,
          hha_agency_id: clinicEditForm.hha_agency_id?.trim() || undefined,
          cms_qies_agency_id:
            clinicEditForm.cms_qies_agency_id?.trim() || undefined,
          cms_branch_id: clinicEditForm.cms_branch_id?.trim() || undefined,
          shp_provider_no: clinicEditForm.shp_provider_no?.trim() || undefined,
          billing_contact_first_name:
            clinicEditForm.billing_contact_first_name?.trim() || undefined,
          billing_contact_last_name:
            clinicEditForm.billing_contact_last_name?.trim() || undefined,
          billing_contact_phone:
            clinicEditForm.billing_contact_phone?.trim() || undefined,
          use_kinnser_wrm: clinicEditForm.use_kinnser_wrm ?? false,
          auto_mrn: clinicEditForm.auto_mrn ?? false,
          using_rcd: clinicEditForm.using_rcd ?? false,
          default_intake_emr_is_roger:
            clinicEditForm.default_intake_emr_is_roger ?? false,
          timezone: clinicEditForm.timezone ?? undefined,
        });
        showSuccess('Clinic created successfully');
        setIsClinicModalOpen(false);
        setClinicEditForm({});
        await onReloadClinics();
      } catch (error) {
        console.error('Failed to create clinic:', error);
        showError('Failed to create clinic');
      } finally {
        setIsSavingClinic(false);
      }
    } else {
      const agencyClinicId = editingClinicId;
      if (agencyClinicId == null) {
        return;
      }
      setIsSavingClinic(true);
      try {
        await updateAgencyClinicAgencyClinicsAgencyClinicIdPut({
          path: { agency_clinic_id: agencyClinicId },
          body: {
            name: clinicEditForm.name || undefined,
            npi: npiSanitized || null,
            ein: normalizeEin(clinicEditForm.ein || '') || undefined,
            address: clinicEditForm.address || undefined,
            city: clinicEditForm.city || undefined,
            state: clinicEditForm.state || undefined,
            zip_code: clinicEditForm.zip_code || undefined,
            phone: clinicEditForm.phone?.trim() || null,
            fax: clinicEditForm.fax?.trim() || null,
            email: clinicEditForm.email?.trim() || null,
            hha_agency_id: clinicEditForm.hha_agency_id?.trim() || null,
            cms_qies_agency_id:
              clinicEditForm.cms_qies_agency_id?.trim() || null,
            cms_branch_id: clinicEditForm.cms_branch_id?.trim() || null,
            shp_provider_no: clinicEditForm.shp_provider_no?.trim() || null,
            billing_contact_first_name:
              clinicEditForm.billing_contact_first_name?.trim() || null,
            billing_contact_last_name:
              clinicEditForm.billing_contact_last_name?.trim() || null,
            billing_contact_phone:
              clinicEditForm.billing_contact_phone?.trim() || null,
            use_kinnser_wrm: clinicEditForm.use_kinnser_wrm ?? false,
            auto_mrn: clinicEditForm.auto_mrn ?? false,
            using_rcd: clinicEditForm.using_rcd ?? false,
            default_intake_emr_is_roger:
              clinicEditForm.default_intake_emr_is_roger ?? false,
            timezone: clinicEditForm.timezone ?? null,
          },
        });
        showSuccess('Clinic updated successfully');
        setIsClinicModalOpen(false);
        setEditingClinicId(null);
        setClinicEditForm({});
        await onReloadClinics();
      } catch (error) {
        console.error('Failed to update clinic:', error);
        showError('Failed to update clinic');
      } finally {
        setIsSavingClinic(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setIsClinicModalOpen(false);
    setEditingClinicId(null);
    setEditingClinicHasKey(false);
    setClinicEditForm({});
  };

  return (
    <>
      {/* Clinic Management Section */}
      <div className='space-y-6'>
        <div className='space-y-1.5'>
          <div className='flex justify-between items-start'>
            <div></div>
            <div className='flex gap-2'>
              {isRogerEmrEnabled && canEdit && (
                <Button onClick={handleCreateClinic} variant='outline'>
                  <Plus className='mr-2 h-4 w-4' />
                  Add Clinic
                </Button>
              )}
              {isKinnserEnabled && canEdit && (
                <Button
                  onClick={handleSyncClinicInfo}
                  disabled={isLoadingClinicInfo}
                  variant='outline'
                >
                  {isLoadingClinicInfo ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <RefreshCw className='mr-2 h-4 w-4' />
                  )}
                  Sync Clinic Info
                </Button>
              )}
            </div>
          </div>
        </div>
        <div>
          <div className='space-y-4'>
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Clinic Name</TableHead>
                    <TableHead>Clinic Key</TableHead>
                    <TableHead>NPI</TableHead>
                    <TableHead>EIN</TableHead>
                    <TableHead>Address</TableHead>
                    {showKinnserColumn && (
                      <TableHead>Mapped to Kinnser?</TableHead>
                    )}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingClinics ? (
                    <TableRow>
                      <TableCell
                        colSpan={clinicTableColSpan}
                        className='text-center py-8'
                      >
                        <Loader2 className='h-6 w-6 animate-spin mx-auto' />
                        <p className='text-sm text-muted-foreground mt-2'>
                          Loading clinics...
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : clinics.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={clinicTableColSpan}
                        className='text-center py-8 text-muted-foreground'
                      >
                        {user?.agency?.id
                          ? isKinnserEnabled
                            ? 'No clinics found. Try syncing clinic info first.'
                            : 'No clinics found. Click "Add Clinic" to create one.'
                          : 'No agency found. Please ensure you are logged in with an agency.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    clinics.map(clinic => (
                      <TableRow key={clinic.id}>
                        <TableCell className='font-medium'>
                          {clinic.name ||
                            (clinic.clinic_key && clinics.length === 1 ? (
                              <span className='text-muted-foreground italic'>
                                Default Clinic
                              </span>
                            ) : (
                              '—'
                            ))}
                        </TableCell>
                        <TableCell className='text-sm text-muted-foreground'>
                          {/* Kinnser: WellSky clinic key. RogerEMR: sequence-assigned key. */}
                          {clinic.clinic_key || '—'}
                        </TableCell>
                        <TableCell>
                          {clinic.npi ? (
                            <Badge variant='secondary'>{clinic.npi}</Badge>
                          ) : (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {clinic.ein ? (
                            <Badge variant='outline'>{clinic.ein}</Badge>
                          ) : (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        <TableCell className='text-sm'>
                          {clinic.address && (
                            <div>
                              {clinic.address}
                              {clinic.city &&
                                clinic.state &&
                                clinic.zip_code && (
                                  <div className='text-muted-foreground'>
                                    {clinic.city}, {clinic.state}{' '}
                                    {clinic.zip_code}
                                  </div>
                                )}
                            </div>
                          )}
                          {!clinic.address && (
                            <span className='text-muted-foreground'>—</span>
                          )}
                        </TableCell>
                        {showKinnserColumn && (
                          <TableCell>
                            <ClinicMappedToKinnser
                              clinic={clinic}
                              insuranceCount={
                                insuranceCountByClinicId.get(clinic.id) ?? 0
                              }
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className='flex items-center gap-2'>
                            {canEdit && (
                              <Button
                                size='sm'
                                variant='outline'
                                onClick={() => handleEditClinic(clinic)}
                              >
                                <Edit className='h-4 w-4' />
                              </Button>
                            )}
                            {/* Deliberately not gated on isKinnserEnabled or
                                canEditClinicInfo. The API's gate is
                                super-user alone, and a cutover ends with the
                                agency Kinnser toggle off, which would take
                                the revert path away at the moment it is
                                needed. */}
                            {isSuperUser &&
                              clinicParticipatesInKinnser(clinic) && (
                                <Button
                                  size='sm'
                                  variant='outline'
                                  aria-label={`Disconnect ${clinic.name} from WellSky sync`}
                                  title='Disconnect from WellSky sync'
                                  onClick={() =>
                                    setKinnserParticipationTarget({
                                      clinic,
                                      action: 'disconnect',
                                    })
                                  }
                                >
                                  <Unlink className='h-4 w-4' />
                                </Button>
                              )}
                            {/* clinic_key is required as well: the route
                                refuses a keyless clinic, because a clinic
                                the scrape can never match would be
                                stranded. */}
                            {isSuperUser &&
                              !clinicParticipatesInKinnser(clinic) &&
                              !!clinic.kinnser_disconnected_at &&
                              !!clinic.clinic_key && (
                                <Button
                                  size='sm'
                                  variant='outline'
                                  aria-label={`Reconnect ${clinic.name} to WellSky sync`}
                                  title='Reconnect to WellSky sync'
                                  onClick={() =>
                                    setKinnserParticipationTarget({
                                      clinic,
                                      action: 'reconnect',
                                    })
                                  }
                                >
                                  <Link2 className='h-4 w-4' />
                                </Button>
                              )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Clinic Create/Edit Modal */}
      <Dialog open={isClinicModalOpen} onOpenChange={setIsClinicModalOpen}>
        <DialogContent className='sm:max-w-[600px] max-h-[85vh] flex flex-col'>
          <DialogHeader>
            <DialogTitle>
              {clinicModalMode === 'create' ? 'Add Clinic' : 'Edit Clinic'}
            </DialogTitle>
            <DialogDescription>
              {clinicModalMode === 'create'
                ? 'Create a new clinic for your agency.'
                : 'Update clinic information.'}
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-4 py-4 overflow-y-auto flex-1 pr-1'>
            <div className='grid gap-2'>
              <label htmlFor='name' className='text-sm font-medium'>
                Clinic Name{clinicModalMode === 'create' && ' *'}
              </label>
              <Input
                id='name'
                value={clinicEditForm.name || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                disabled={clinicModalMode === 'edit' && editingClinicHasKey}
              />
              {clinicModalMode === 'edit' && editingClinicHasKey && (
                <p className='text-xs text-muted-foreground'>
                  Name is synced from an external source and cannot be edited.
                </p>
              )}
            </div>
            <div className='grid gap-2'>
              <label htmlFor='npi' className='text-sm font-medium'>
                NPI
              </label>
              <NpiInput
                id='npi'
                value={clinicEditForm.npi || ''}
                onValueChange={npi =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    npi,
                  }))
                }
                placeholder='10-digit NPI'
                // NpiInput has its own error line; CMS's wording takes it.
                externalError={cmsErrors.npi}
              />
            </div>
            <div className='grid gap-2'>
              <label htmlFor='ein' className='text-sm font-medium'>
                EIN
              </label>
              <Input
                id='ein'
                value={clinicEditForm.ein || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    ein: normalizeEin(e.target.value),
                  }))
                }
                placeholder='Digits only (no hyphens)'
              />
            </div>
            <div className='grid gap-2'>
              <label htmlFor='phone' className='text-sm font-medium'>
                Phone
              </label>
              <Input
                id='phone'
                value={clinicEditForm.phone || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    phone: formatPhone(e.target.value),
                  }))
                }
                placeholder='555-123-4567'
              />
            </div>
            <div className='grid gap-2'>
              <label htmlFor='fax' className='text-sm font-medium'>
                Fax
              </label>
              <Input
                id='fax'
                value={clinicEditForm.fax || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    fax: formatPhone(e.target.value),
                  }))
                }
                placeholder='555-123-4567'
              />
            </div>
            <div className='grid gap-2'>
              <label htmlFor='email' className='text-sm font-medium'>
                Contact email
              </label>
              <Input
                id='email'
                type='email'
                value={clinicEditForm.email || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    email: e.target.value,
                  }))
                }
                placeholder='info@agency.com'
              />
              <p className='text-xs text-muted-foreground'>
                The office&apos;s general inbox. Used for payer enrollment.
              </p>
            </div>
            <div className='grid gap-2'>
              <label htmlFor='hha_agency_id' className='text-sm font-medium'>
                CCN / PTAN / Medicare Provider Number
              </label>
              <Input
                id='hha_agency_id'
                value={clinicEditForm.hha_agency_id || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    // Strip non-alphanumeric and cap at 6 in the handler (not via
                    // DOM maxLength) so a value pasted with spaces/separators is
                    // recovered to the 6-char alphanumeric value OASIS M0010 accepts
                    // instead of being truncated before we can clean it.
                    hha_agency_id: e.target.value
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(0, 6),
                  }))
                }
                placeholder='e.g. 067890'
              />
              <p className='text-xs text-muted-foreground'>
                CMS Certification Number (6 letters or digits). Prefills M0010
                on this clinic's OASIS forms.
              </p>
            </div>
            <div className='grid gap-2'>
              <label
                htmlFor='cms_qies_agency_id'
                className='text-sm font-medium'
              >
                HHA Agency ID
              </label>
              <Input
                id='cms_qies_agency_id'
                value={clinicEditForm.cms_qies_agency_id || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    cms_qies_agency_id: e.target.value,
                  }))
                }
                maxLength={16}
                placeholder='e.g. NVS3881HHA'
                aria-invalid={!!cmsErrors.cms_qies_agency_id || undefined}
                aria-describedby={
                  cmsErrors.cms_qies_agency_id
                    ? cmsFieldErrorId('cms_qies_agency_id')
                    : undefined
                }
              />
              <CmsFieldError
                fieldId='cms_qies_agency_id'
                message={cmsErrors.cms_qies_agency_id}
              />
              <p className='text-xs text-muted-foreground'>
                CMS QIES identification number, usually assigned by the State
                OASIS Coordinator. Sets HHA_AGENCY_ID in the OASIS Export.
              </p>
            </div>
            <div className='grid gap-2'>
              <label htmlFor='cms_branch_id' className='text-sm font-medium'>
                Branch ID
              </label>
              <Input
                id='cms_branch_id'
                value={clinicEditForm.cms_branch_id || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    cms_branch_id: e.target.value,
                  }))
                }
                maxLength={10}
                placeholder='N'
              />
              <p className='text-xs text-muted-foreground'>
                CMS branch ID. Prefills M0016 on this clinic's OASIS forms.
                Enter N if this clinic has no branches.
              </p>
            </div>
            <div className='grid gap-2'>
              <label htmlFor='shp_provider_no' className='text-sm font-medium'>
                SHP Provider Number
              </label>
              <Input
                id='shp_provider_no'
                value={clinicEditForm.shp_provider_no || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    shp_provider_no: e.target.value,
                  }))
                }
                maxLength={16}
                placeholder='e.g. 48400'
                aria-invalid={!!cmsErrors.shp_provider_no || undefined}
                aria-describedby={
                  cmsErrors.shp_provider_no
                    ? cmsFieldErrorId('shp_provider_no')
                    : undefined
                }
              />
              <CmsFieldError
                fieldId='shp_provider_no'
                message={cmsErrors.shp_provider_no}
              />
              <p className='text-xs text-muted-foreground'>
                SHP (Strategic Healthcare Programs) provider number for this
                clinic. Used in the CAHPS export and on the nightly OASIS batch
                to SHP.
              </p>
            </div>
            {/* Not behind ADMIN_TAB_BILLING_VIEW: a NOA cannot be sent without
                this contact, and it is no more sensitive than the NPI and EIN
                above. The dialog only opens for an edit grant. Roger EMR
                agencies only: an intake-only agency sends no claims. */}
            {isRogerEmrEnabled && (
              <div className='grid gap-2'>
                <span className='text-sm font-medium'>Billing Contact</span>
                <div className='grid grid-cols-2 gap-2'>
                  <Input
                    id='billing_contact_first_name'
                    aria-label='Billing contact first name'
                    value={clinicEditForm.billing_contact_first_name || ''}
                    onChange={e =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        billing_contact_first_name: e.target.value,
                      }))
                    }
                    maxLength={35}
                    placeholder='First name'
                  />
                  <Input
                    id='billing_contact_last_name'
                    aria-label='Billing contact last name'
                    value={clinicEditForm.billing_contact_last_name || ''}
                    onChange={e =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        billing_contact_last_name: e.target.value,
                      }))
                    }
                    maxLength={60}
                    placeholder='Last name'
                  />
                </div>
                <Input
                  id='billing_contact_phone'
                  aria-label='Billing contact phone'
                  value={clinicEditForm.billing_contact_phone || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      billing_contact_phone: formatPhone(e.target.value),
                    }))
                  }
                  placeholder='555-123-4567'
                />
                <p className='text-xs text-muted-foreground'>
                  Sent on claims as the submitter contact the payer can call
                  about a transmission.
                </p>
              </div>
            )}
            {isSuperUser && (
              <>
                <div className='flex items-start gap-3'>
                  <Switch
                    id='use_kinnser_wrm'
                    checked={clinicEditForm.use_kinnser_wrm ?? false}
                    onCheckedChange={checked =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        use_kinnser_wrm: checked,
                      }))
                    }
                  />
                  <div className='grid gap-1'>
                    <label
                      htmlFor='use_kinnser_wrm'
                      className='text-sm font-medium leading-none'
                    >
                      Use Kinnser WRM
                    </label>
                    <p className='text-xs text-muted-foreground'>
                      Toggle Kinnser Wellsky Resource Manager for this clinic.
                      This setting is persisted in RogerEMR and is not changed
                      by the Kinnser clinic info sync.
                    </p>
                  </div>
                </div>
                <div className='flex items-start gap-3'>
                  <Switch
                    id='auto_mrn'
                    checked={clinicEditForm.auto_mrn ?? false}
                    onCheckedChange={checked =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        auto_mrn: checked,
                      }))
                    }
                  />
                  <div className='grid gap-1'>
                    <label
                      htmlFor='auto_mrn'
                      className='text-sm font-medium leading-none'
                    >
                      Auto-assign MRN on intake (Roger EMR)
                    </label>
                    <p className='text-xs text-muted-foreground'>
                      When enabled, Roger EMR allocates MRNs automatically when
                      an intake is pushed to Roger for this clinic. When pushing
                      to Kinnser, MRN behavior comes from the WellSky catalog in
                      the Roger App instead.
                    </p>
                  </div>
                </div>
                <div className='flex items-start gap-3'>
                  <Switch
                    id='using_rcd'
                    checked={clinicEditForm.using_rcd ?? false}
                    onCheckedChange={checked =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        using_rcd: checked,
                      }))
                    }
                  />
                  <div className='grid gap-1'>
                    <label
                      htmlFor='using_rcd'
                      className='text-sm font-medium leading-none'
                    >
                      Using RCD (Pre-Claim Review)
                    </label>
                    <p className='text-xs text-muted-foreground'>
                      When enabled, episodes for this clinic show Pre-Claim
                      Review fields. This setting is persisted in RogerEMR and
                      is not changed by the Kinnser clinic info sync.
                    </p>
                  </div>
                </div>
                <div className='flex items-start gap-3'>
                  <Switch
                    id='default_intake_emr_is_roger'
                    checked={
                      clinicEditForm.default_intake_emr_is_roger ?? false
                    }
                    onCheckedChange={checked =>
                      setClinicEditForm(prev => ({
                        ...prev,
                        default_intake_emr_is_roger: checked,
                      }))
                    }
                  />
                  <div className='grid gap-1'>
                    <label
                      htmlFor='default_intake_emr_is_roger'
                      className='text-sm font-medium leading-none'
                    >
                      Default Intake EMR to RogerEMR
                    </label>
                    <p className='text-xs text-muted-foreground'>
                      When enabled, the Intake Extraction step defaults to
                      RogerEMR for this clinic (instead of the external Kinnser
                      EMR) when the agency has both EMRs. Users can still
                      switch. This setting is persisted in RogerEMR and is not
                      changed by the Kinnser clinic info sync.
                    </p>
                  </div>
                </div>
              </>
            )}
            <div className='grid gap-2'>
              <label htmlFor='address' className='text-sm font-medium'>
                Address
              </label>
              <Input
                id='address'
                value={clinicEditForm.address || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    address: e.target.value,
                  }))
                }
              />
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div className='grid gap-2'>
                <label htmlFor='city' className='text-sm font-medium'>
                  City
                </label>
                <Input
                  id='city'
                  value={clinicEditForm.city || ''}
                  onChange={e =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      city: e.target.value,
                    }))
                  }
                />
              </div>
              <div className='grid gap-2'>
                <label htmlFor='state' className='text-sm font-medium'>
                  State
                </label>
                <Select
                  value={clinicEditForm.state || 'none'}
                  onValueChange={value =>
                    setClinicEditForm(prev => ({
                      ...prev,
                      state: value === 'none' ? null : value,
                    }))
                  }
                >
                  <SelectTrigger id='state'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='none'>None</SelectItem>
                    {US_STATES.map(state => (
                      <SelectItem key={state.value} value={state.value}>
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <CmsFieldError fieldId='state' message={cmsErrors.state} />
              </div>
            </div>
            <div className='grid gap-2'>
              <label htmlFor='zip_code' className='text-sm font-medium'>
                ZIP Code
              </label>
              <Input
                id='zip_code'
                value={clinicEditForm.zip_code || ''}
                onChange={e =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    zip_code: e.target.value,
                  }))
                }
              />
            </div>
            <div className='grid gap-2'>
              <label htmlFor='timezone' className='text-sm font-medium'>
                Timezone
              </label>
              <VirtualizedCombobox
                id='timezone'
                options={TIMEZONE_OPTIONS}
                value={clinicEditForm.timezone || ''}
                onValueChange={value =>
                  setClinicEditForm(prev => ({
                    ...prev,
                    timezone: value || null,
                  }))
                }
                placeholder='Use agency default'
                searchPlaceholder='Search timezones...'
                height='300px'
              />
              <p className='text-xs text-muted-foreground'>
                When unset, this clinic uses the agency's timezone.
              </p>
            </div>
          </div>
          <DialogFooter>
            {shpNumberUnsaveable && (
              <p
                id='clinic-save-blocked'
                className='mr-auto self-center text-sm text-destructive'
              >
                Fix the SHP Provider Number to save.
              </p>
            )}
            <Button variant='outline' onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveClinic}
              aria-describedby={
                shpNumberUnsaveable ? 'clinic-save-blocked' : undefined
              }
              disabled={
                isSavingClinic ||
                shpNumberUnsaveable ||
                (clinicModalMode === 'create' && !clinicEditForm.name?.trim())
              }
            >
              {isSavingClinic && (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              )}
              {clinicModalMode === 'create' ? 'Create Clinic' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Kinnser Participation Confirmation. A Dialog, never window.confirm:
          a window dialog wedges browser automation. */}
      <Dialog
        open={kinnserParticipationTarget !== null}
        onOpenChange={open => {
          if (!open) setKinnserParticipationTarget(null);
        }}
      >
        {/* Gated on the target rather than only on its fields. Left
            mounted, DialogContent survives a 200ms exit animation, and a
            body reading through `?.` would flash blank values while the
            ternary flipped a closing Reconnect dialog into a red
            Disconnect one. Unmounting it costs that exit animation and
            buys never showing the wrong action's copy. */}
        {kinnserParticipationTarget && (
          <DialogContent className='sm:max-w-[480px]'>
            {kinnserParticipationTarget.action === 'reconnect' ? (
              <>
                <DialogHeader>
                  <DialogTitle>Reconnect to WellSky sync</DialogTitle>
                  <DialogDescription>
                    WellSky will own {kinnserParticipationTarget.clinic.name}{' '}
                    (clinic key {kinnserParticipationTarget.clinic.clinic_key})
                    again. Its insurances, physicians and staff go back to being
                    updated by the clinic info sync, and new payers can no
                    longer be added in Roger. The next sync confirms
                    participation.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => setKinnserParticipationTarget(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmKinnserParticipationChange}
                    disabled={isChangingKinnserParticipation}
                  >
                    {isChangingKinnserParticipation && (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    )}
                    Reconnect
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <DialogHeader>
                  <DialogTitle>Disconnect from WellSky sync</DialogTitle>
                  <DialogDescription>
                    Roger will own {kinnserParticipationTarget.clinic.name}{' '}
                    (clinic key {kinnserParticipationTarget.clinic.clinic_key}).
                    WellSky will stop updating this clinic's insurances,
                    physicians and staff, and you will be able to add payers to
                    it in Roger. The clinic key is kept, so this can be
                    reconnected later.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => setKinnserParticipationTarget(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='destructive'
                    onClick={handleConfirmKinnserParticipationChange}
                    disabled={isChangingKinnserParticipation}
                  >
                    {isChangingKinnserParticipation && (
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    )}
                    Disconnect
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
};

export default ClinicInfoSection;
